import { Box, Typography } from '@mui/material'
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { FirmDashboardMatterStatusCount } from '../../../../generated'
import ChartCard from '../cases/caseCharts/ChartCard'
import { CHART_HEIGHT } from '../cases/caseCharts/constants'
import { CARD_SUBTITLE_SX, MATTER_STATUS_SERIES, formatCount } from './constants'

const INK = 'rgba(0, 0, 0, 0.87)'
/** Each category holds one bar per series, so a horizontal row is taller than a single-bar chart's. */
const ROW_HEIGHT = 52
const AXIS_ALLOWANCE = 40
/** ChartCard's plot area padding (p={2}), top + bottom. */
const CARD_PADDING = 32
const LEGEND_HEIGHT = 28
/** Past this the horizontal chart scrolls inside its card instead of growing the page. */
const MAX_HORIZONTAL_HEIGHT = 480

interface MatterStatusBarChartProps {
  title: string
  subtitle?: string
  tooltip?: string
  data?: FirmDashboardMatterStatusCount[]
  loading?: boolean
  /** 'horizontal' for long, open-ended category lists (law types). */
  orientation?: 'vertical' | 'horizontal'
  categoryWidth?: number
}

/** Matters per category, as grouped bars: the total next to its Active / Completed split. */
const MatterStatusBarChart = ({
  title,
  subtitle,
  tooltip,
  data = [],
  loading,
  orientation = 'vertical',
  categoryWidth = 130
}: MatterStatusBarChartProps) => {
  const isHorizontal = orientation === 'horizontal'
  // Horizontal bars keep a fixed row height, so the plot grows with the list and scrolls in the card
  const plotHeight = isHorizontal ? data.length * ROW_HEIGHT + AXIS_ALLOWANCE : '100%'
  const cardHeight = isHorizontal
    ? Math.min(
        Math.max(data.length * ROW_HEIGHT + AXIS_ALLOWANCE + CARD_PADDING + LEGEND_HEIGHT, 200),
        MAX_HORIZONTAL_HEIGHT
      )
    : CHART_HEIGHT
  const rows = data.map(({ label, count, active, completed }) => ({ name: label, count, active, completed }))

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      subtitleSx={CARD_SUBTITLE_SX}
      tooltip={tooltip}
      loading={loading}
      isEmpty={!data.some(({ count }) => count !== 0)}
      emptyMessage="No data for the selected period."
      height={cardHeight}
    >
      <Box height="100%" display="flex" flexDirection="column">
        {/* Outside the scroll area, so the legend stays in view while a long list scrolls */}
        <Box height={LEGEND_HEIGHT} display="flex" justifyContent="center" alignItems="center" gap={2} flexShrink={0}>
          {MATTER_STATUS_SERIES.map(({ key, label, color }) => (
            <Box key={key} display="flex" alignItems="center" gap={0.75}>
              <Box width={10} height={10} bgcolor={color} borderRadius="2px" />
              <Typography fontSize={12} color={INK}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box flex={1} minHeight={0} overflow="auto">
          <Box height={plotHeight} minWidth={isHorizontal ? 360 : undefined}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rows}
                layout={isHorizontal ? 'vertical' : 'horizontal'}
                barGap={2}
                margin={
                  isHorizontal ? { top: 8, right: 48, left: 8, bottom: 8 } : { top: 24, right: 8, left: 8, bottom: 8 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" vertical={isHorizontal} horizontal={!isHorizontal} />
                {isHorizontal ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={formatCount} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={categoryWidth} interval={0} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCount} />
                  </>
                )}
                <Tooltip formatter={(value: number) => formatCount(value)} cursor={{ fillOpacity: 0.3 }} />
                {MATTER_STATUS_SERIES.map(({ key, label, color }) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={label}
                    fill={color}
                    radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                    maxBarSize={isHorizontal ? 14 : 32}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey={key}
                      position={isHorizontal ? 'right' : 'top'}
                      fontSize={11}
                      fill={INK}
                      formatter={formatCount}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </ChartCard>
  )
}

export default MatterStatusBarChart
